import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import CreateAppointmentService from "modules/appointments/service/CreateAppointmentService";
import EventService from "service/event/EventService";
import "../styles/createAppointment.scss";

/**
 * Backfill a booking-for person's missing Date of birth / Email before an
 * appointment can be created — bridge's "Update missing profile details"
 * dialog. Only the genuinely-missing fields are editable; the PUT body mirrors
 * bridge's updateProfileInfo field-for-field. On success the caller gets the
 * patched record so the cascade can continue with valid demographics.
 */

/** epoch-millis (or a Date) → "YYYY-MM-DD", else "". */
const toYmd = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const d = new Date(typeof v === "string" ? Number(v) || v : v);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MissingProfileDialog = ({ patient, needDob, needEmail, onSaved, onClose }) => {
  const service = useRef(new CreateAppointmentService()).current;
  const mounted = useRef(true);
  const panelRef = useRef(null);
  const lastFocused = useRef(null);

  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    mounted.current = true;
    lastFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (panelRef.current) panelRef.current.focus();
    return () => {
      mounted.current = false;
      document.body.style.overflow = prevOverflow;
      if (lastFocused.current && lastFocused.current.focus) lastFocused.current.focus();
    };
  }, []);

  const name = [patient.firstName, patient.lastName].filter(Boolean).join(" ").trim();

  const save = () => {
    if (saving) return;
    if (needDob && !dob) return setError("Enter the date of birth.");
    if (needEmail && !EMAIL_RE.test(email.trim())) {
      return setError("Enter a valid email address.");
    }
    setError("");
    setSaving(true);
    const body = {
      patientId: patient.id,
      firstName: patient.firstName,
      lastName: patient.lastName ? patient.lastName : null,
      gender: patient.gender,
      dateOfBirth: needDob ? dob : toYmd(patient.dateOfBirth),
      phoneNo: patient.phoneno != null ? String(patient.phoneno) : "",
      email: needEmail ? email.trim() : patient.email,
      profileImage: patient.profileImage,
      countryCode: patient.countryCode,
    };
    service.updateProfileInfo(
      body,
      () => {
        if (!mounted.current) return;
        setSaving(false);
        // Hand back the patched record so the cascade sees valid demographics.
        onSaved({
          ...patient,
          dateOfBirth: needDob ? new Date(dob).getTime() : patient.dateOfBirth,
          email: needEmail ? email.trim() : patient.email,
        });
      },
      (err) => {
        if (!mounted.current) return;
        setSaving(false);
        EventService.emit("showError", {
          message: `Couldn't update profile: ${(err && err.message) || "please try again."}`,
        });
      }
    );
  };

  return ReactDOM.createPortal(
    <div
      className="mp-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="mp-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Update missing profile details"
        ref={panelRef}
        tabIndex={-1}
      >
        <h2 className="mp-title">Complete {name || "the patient"}&rsquo;s profile</h2>
        <p className="mp-sub">
          An appointment needs a date of birth and email. Fill the missing
          {needDob && needEmail ? " details" : needDob ? " date of birth" : " email"} to continue.
        </p>

        {needDob && (
          <div className="cf-field">
            <label htmlFor="mp-dob">Date of birth</label>
            <input
              id="mp-dob"
              type="date"
              className="cf-input"
              max={toYmd(Date.now())}
              value={dob}
              onChange={(e) => {
                setDob(e.target.value);
                setError("");
              }}
            />
          </div>
        )}

        {needEmail && (
          <div className="cf-field">
            <label htmlFor="mp-email">Email ID</label>
            <input
              id="mp-email"
              type="email"
              className="cf-input"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
            />
          </div>
        )}

        {error && <p className="cf-error">{error}</p>}

        <div className="mp-actions">
          <button type="button" className="zy-btn zy-btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="zy-btn zy-btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

MissingProfileDialog.propTypes = {
  /** The record missing fields — carries id, name, gender, phoneno, countryCode. */
  patient: PropTypes.object.isRequired,
  needDob: PropTypes.bool.isRequired,
  needEmail: PropTypes.bool.isRequired,
  /** Called with the patched record once the PUT succeeds. */
  onSaved: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MissingProfileDialog;
